import React, { useCallback, useEffect, useRef, useState } from 'react'

import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { Typography, useTheme } from '@mui/material'
import Box from '@mui/material/Box'

type OrganizationIdCopyProps = {
  organizationId?: string
}

const CopyToClipboard = ({ organizationId }: OrganizationIdCopyProps) => {
  const theme = useTheme()
  const [isCopied, setIsCopied] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleCopy = useCallback(async () => {
    if (!organizationId) return

    try {
      await navigator.clipboard.writeText(organizationId)
      setIsCopied(true)

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(() => {
        setIsCopied(false)
        timeoutRef.current = null
      }, 1500)
    } catch (_error) {
      setIsCopied(false)
    }
  }, [organizationId])

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  if (!organizationId) {
    return null
  }

  return (
    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', ml: 1 }}>
      {isCopied ? (
        <CheckCircleIcon fontSize="small" color="primary" sx={{ pointerEvents: 'none' }} />
      ) : (
        <ContentCopyIcon
          sx={{
            cursor: 'pointer',
            color: 'grey.600',
            transition: theme.transitions.create(['color', 'transform'], {
              easing: theme.transitions.easing.easeOut,
            }),
            '&:hover': { color: 'grey.800' },
          }}
          fontSize="small"
          onClick={handleCopy}
        />
      )}
    </Box>
  )
}

export default CopyToClipboard
